import moment from 'moment-mini'
import { of } from 'rxjs'
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { Member } from 'src/app/features/member/member'
import { environment } from 'src/environments/environment'

import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { AppState } from '../core.state'
import { LocalStorageService } from '../local-storage.service'
import { NotificationService } from '../notification.service'
import { routerSelectors } from '../router/router.selectors'
import { settingsActions } from '../settings/settings.actions'
import { authActions } from './auth.actions'
import { authSelectors } from './auth.selectors'
import { AuthService } from './auth.service'
import { TokenResponse } from './interfaces'
import { oidcActions } from './oidc/oidc.actions'
import { oidcSelectors } from './oidc/oidc.selectors'
import { persistentUserRoleActions } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.actions'
import { persistentUserRoleSelectors } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.selector'

const OIDC_REAUTH_ATTEMPTED_AT_KEY = 'delcom_oidc_reauth_attempted_at'
const OIDC_REAUTH_MIN_INTERVAL_SECONDS = 120
const AUTH_PAGES = ['/pages/login', '/oidc/auth-callback']

@Injectable()
export class AuthEffects implements OnInitEffects {
  private oidcReauthenticationRedirectStarted = false

  constructor(
    private actions$: Actions,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private store: Store<AppState>
  ) {}

  ngrxOnInitEffects() {
    this.store.dispatch(authActions.resetIsLoggedIn())
    return authActions.attemptAuthFromStorage()
  }

  login = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.login),
      exhaustMap((authLogin) =>
        this.authService.login(authLogin.email, authLogin.password).pipe(
          map((tokenResponse: TokenResponse) =>
            authActions.authenticationSuccess({ tokenResponse })
          ),
          catchError((error) => of(authActions.loginFailure({ error })))
        )
      )
    )
  )

  oidcAuthenticationSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(oidcActions.loadTokenResponse),
      tap((action) => {
        console.log('Oidc login success', action)
        window.sessionStorage.removeItem(OIDC_REAUTH_ATTEMPTED_AT_KEY)
        this.store.dispatch(authActions.setIsOidc({ isOidc: true }))
      }),
      map((action) => {
        const tokenResponse: TokenResponse = action.tokenResponse
        return authActions.authenticationSuccess({ tokenResponse })
      })
    )
  )

  attemptAuthFromStorage = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.attemptAuthFromStorage),
      filter(() => !this.isOnAuthCallbackPage()),
      withLatestFrom(
        this.store.select(authSelectors.selectTokenResponse),
        this.store.select(authSelectors.selectIsOidc)
      ),
      filter(([action, tokenResponse, isOidc]) =>
        Boolean(tokenResponse && tokenResponse.accessToken)
      ),
      map(([action, tokenResponse, isOidc]) => {
        const parsedAccessToken = this.authService.parseToken(
          tokenResponse?.accessToken
        )

        if (!parsedAccessToken) {
          console.log('Parse authentication token exception, will call logout')
          return authActions.logout()
        }

        if (parsedAccessToken.exp < moment().unix()) {
          console.log('Access token has expired, will attempt a token refresh')

          if (isOidc) {
            return this.resolveOidcRefreshAction(tokenResponse)
          }

          return authActions.attemptTokenRefresh({
            refreshToken: tokenResponse.refreshToken
          })
        } else {
          return authActions.authenticationSuccess({
            tokenResponse
          })
        }
      })
    )
  )

  fetchMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.authenticationSuccess, authActions.fetchMember),
      exhaustMap((fetchMember) =>
        this.authService.getMember().pipe(
          map((member) => authActions.loadMember({ member })),

          catchError((error) => of(authActions.loginFailure({ error })))
        )
      )
    )
  )

  onLoadMember = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.loadMember),
        withLatestFrom(this.store.select(routerSelectors.selectUrl)),
        tap(([action, currentUrl]) =>
          this.store.dispatch(
            settingsActions.setOneById({
              id: 'language',
              value: action.member.language
                ? action.member.language
                : environment.defaultLanguage
            })
          )
        ),
        tap(([action, currentUrl]) => {
          this.store
            .select(persistentUserRoleSelectors.selectSelectedUserRole)
            .pipe(take(1))
            .subscribe((selectedUserRole) => {
              let updatedUserRole = action.member?.userRoles?.find(
                (uR) => uR?.id == selectedUserRole?.id
              )

              if (updatedUserRole) {
                this.store.dispatch(
                  persistentUserRoleActions.updateSelectedUserRole({
                    userRole: updatedUserRole
                  })
                )
              }
            })
        }),

        filter(([action, currentUrl]) => Boolean(currentUrl)),
        filter(([action, currentUrl]) =>
          Boolean(
            [
              '/pages/login',
              '/pages/register',
              '/pages/reset',
              '/oidc/auth-callback'
            ].find((url) => currentUrl.includes(url))
          )
        ),
        withLatestFrom(this.store.select(authSelectors.selectRedirectUrl)),
        tap(([action, redirectUrl]) => {
          console.log(`Will redirect to ${redirectUrl}`)

          return this.router.navigateByUrl(redirectUrl)
        })
      ),
    {
      dispatch: false
    }
  )

  onUnauthorizedTokenRefresh = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.onUnauthorizedTokenRefresh),
      withLatestFrom(
        this.store.select(authSelectors.selectIsOidc),
        this.store.select(authSelectors.selectTokenResponse),
        this.store.select(routerSelectors.selectUrl),
        this.store.select(authSelectors.selectIsLoggedIn)
      ),
      filter(([action, isOidc, tokenReponse, currentUrl, isLoggedIn]) =>
        Boolean(
          !['/pages/login', '/oidc/auth-callback'].find((url) =>
            currentUrl?.includes(url)
          )
        )
      ),
      map(([action, isOidc, tokenResponse, currentUrl, isLoggedIn]) => {
        if (!tokenResponse) {
          console.log(
            'onUnauthorizedTokenRefresh got no tokenReponse, and will preform logout'
          )

          if (isLoggedIn) {
            this.notificationService.emit(
              'alerts.authSessionFailure',
              'warning'
            )
          }

          return authActions.logout()
        }

        if (isOidc) {
          console.log(
            'onUnauthorizedTokenRefresh will attempt oidc token refresh'
          )

          return this.resolveOidcRefreshAction(tokenResponse)
        }

        console.log(
          'onUnauthorizedTokenRefresh will attempt non oidc token refresh'
        )

        return authActions.attemptTokenRefresh({
          refreshToken: tokenResponse.refreshToken
        })
      })
    )
  )

  attemptTokenRefresh = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.attemptTokenRefresh),
      exhaustMap((attemptTokenRefresh) =>
        this.authService.tokenRefresh(attemptTokenRefresh.refreshToken).pipe(
          map((tokenResponse: TokenResponse) =>
            authActions.authenticationSuccess({ tokenResponse })
          ),
          catchError((error) => {
            console.log(
              'Token refresh failed, resetting auth state using logout'
            )

            this.store
              .select(authSelectors.selectIsLoggedIn)
              .pipe(take(1))
              .subscribe((isLoggedIn) => {
                if (isLoggedIn) {
                  this.notificationService.emit(
                    'alerts.authSessionFailure',
                    'warning'
                  )
                }
              })

            return of(authActions.logout())
          })
        )
      )
    )
  )

  attemptOidcReauthentication = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.attemptOidcReauthentication),
      filter(() => !this.oidcReauthenticationRedirectStarted),
      withLatestFrom(
        this.store.select(oidcSelectors.selectProviderSettings),
        this.store.select(oidcSelectors.selectProviderConfig)
      ),
      map(([action, providerSettings, providerConfig]) => {
        if (
          !providerSettings ||
          !providerConfig ||
          this.isOnAuthPage() ||
          !this.markOidcReauthenticationAttempt()
        ) {
          console.log('Oidc re-authentication not possible, will logout')
          return authActions.logout()
        }

        this.oidcReauthenticationRedirectStarted = true
        this.saveCurrentUrlForPostLoginRedirect()
        console.log('Will redirect to oidc provider for re-authentication')
        return oidcActions.startAuth()
      })
    )
  )

  updateMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.updateMember),
      exhaustMap((updateMember) =>
        this.authService
          .updateMember(updateMember.id, updateMember.params)
          .pipe(map((member) => authActions.loadMember({ member })))
      )
    )
  )

  loginFailure = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.loginFailure),
        tap((action) => {
          console.error(action.error)
          //   this.notificationService.emit('alerts.loginFailure', 'warning')
        })
      ),
    { dispatch: false }
  )

  register = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.register),
      exhaustMap((action) =>
        this.authService.register(action.member).pipe(
          map((tokenResponse: TokenResponse) =>
            authActions.authenticationSuccess({ tokenResponse })
          ),
          catchError((error) => {
            console.error(error)
            // TODO show registration error
            return of(authActions.loginFailure({ error }))
          })
        )
      )
    )
  )

  logout = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.logout),
        tap(() => {
          this.router.navigateByUrl('/pages/login')
          this.localStorageService.clear()
        })
      ),
    { dispatch: false }
  )

  resetPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.resetPassword),
      exhaustMap((action) =>
        this.authService.changePassword(action.password).pipe(
          map((memberResponse: Member) =>
            authActions.login({
              email: memberResponse.email,
              password: action.password
            })
          ),
          catchError((error) => of(authActions.resetPasswordFailure({ error })))
        )
      )
    )
  )

  sendPasswordResetEamil = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.sendPasswordResetEmail),
      exhaustMap((action) =>
        this.authService
          .sendPasswordResetEmail(action.email, action.resetPath)
          .pipe(
            map((result: boolean) =>
              authActions.sendPasswordResetEmailSuccess({ result: !result })
            ),
            catchError((error) =>
              of(authActions.sendPasswordResetEmailFailure({ error }))
            )
          )
      )
    )
  )

  changePassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.changePassword),
      exhaustMap((action) =>
        this.authService
          .changePassword(action.password, action.currentPassword)
          .pipe(
            map(() => authActions.changePasswordSucces()),
            catchError((error) =>
              of(authActions.changePasswordFailure({ error }))
            )
          )
      )
    )
  )

  changePasswordFailure = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.changePasswordFailure),
        tap((action) => {
          console.error(action.error)
          this.notificationService.emit('alerts.passwordChangedFail', 'error')
        })
      ),
    { dispatch: false }
  )

  // changePasswordSuccess = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(authActions.changePasswordSucces),
  //       tap((action) => {
  //         this.notificationService.emit(
  //           'alerts.passwordChangedSuccess',
  //           'success'
  //         )
  //       })
  //     ),
  //   { dispatch: false }
  // )

  checkPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.checkPassword),
      exhaustMap((authLogin) =>
        this.authService.login(authLogin.email, authLogin.password).pipe(
          map((tokenResponse: TokenResponse) =>
            authActions.checkPasswordSuccess({ tokenResponse })
          ),
          catchError((error) => of(authActions.loginFailure({ error })))
        )
      )
    )
  )

  anonymizeMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.anonymizeMember),
      exhaustMap((action) =>
        this.authService.anonymizeMember(action.memberId).pipe(
          map(() => authActions.anonymizeMemberSuccess()),
          tap((action) => {
            this.notificationService.emit('alerts.success', 'success')
          }),
          catchError((error) =>
            of(authActions.anonymizeMemberFailure({ error }))
          )
        )
      )
    )
  )

  private resolveOidcRefreshAction(tokenResponse: TokenResponse) {
    if (!tokenResponse.refreshToken?.length) {
      console.log(
        'No oidc refresh token, will attempt silent re-authentication'
      )
      return authActions.attemptOidcReauthentication()
    }

    const parsedRefreshToken = this.authService.parseToken(
      tokenResponse.refreshToken
    )

    // opaque refresh tokens (Entra) cannot be parsed, attempt refresh anyway
    if (!parsedRefreshToken) {
      console.warn(
        'Failed to parse oidc refresh token, will just attempt refresh'
      )
      return oidcActions.fetchTokenResponseByRefreshToken({
        refreshToken: tokenResponse.refreshToken
      })
    }

    if (parsedRefreshToken.exp < moment().unix()) {
      console.log(
        'OIDC refresh token expired, will attempt silent re-authentication'
      )
      return authActions.attemptOidcReauthentication()
    }

    return oidcActions.fetchTokenResponseByRefreshToken({
      refreshToken: tokenResponse.refreshToken
    })
  }

  private isOnAuthPage(): boolean {
    const currentUrl = this.router.url
    return AUTH_PAGES.some((page) => currentUrl?.includes(page))
  }

  private isOnAuthCallbackPage(): boolean {
    return window.location.pathname.includes('/oidc/auth-callback')
  }

  private secondsSinceLastOidcReauthenticationAttempt(): number {
    const lastAttempt = Number(
      window.sessionStorage.getItem(OIDC_REAUTH_ATTEMPTED_AT_KEY)
    )

    if (!lastAttempt) {
      return Number.POSITIVE_INFINITY
    }

    return moment().unix() - lastAttempt
  }

  private markOidcReauthenticationAttempt(): boolean {
    if (
      this.secondsSinceLastOidcReauthenticationAttempt() <
      OIDC_REAUTH_MIN_INTERVAL_SECONDS
    ) {
      return false
    }

    window.sessionStorage.setItem(
      OIDC_REAUTH_ATTEMPTED_AT_KEY,
      String(moment().unix())
    )
    return true
  }

  private saveCurrentUrlForPostLoginRedirect(): void {
    const currentUrl = window.location.pathname + window.location.search

    this.store.dispatch(authActions.setRedirectUrl({ redirectUrl: currentUrl }))
  }
}
